import './modules-manage.css'
import { faEyeSlash, faGamepad, faPlug, faQuestionCircle, type IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useState } from 'react'
import { ModuleInstanceType } from '@companion-app/shared/Model/Instance.js'
import { StaticAlert } from '~/Components/Alert.js'
import { Button, ButtonGroup } from '~/Components/Button'
import { NonIdealState } from '~/Components/NonIdealState.js'
import { SearchBox } from '~/Components/SearchBox.js'
import { TabArea } from '~/Components/TabArea.js'
import { Table } from '~/Components/Table.js'
import { useTableVisibilityHelper, VisibilityButton } from '~/Components/TableVisibility.js'
import { filterProducts, useAllModuleProducts, type FuzzyProduct } from '~/Hooks/useFilteredProducts.js'
import { ContextHelpButton } from '~/Layout/PanelIcons.js'
import { assertNever, makeAbsolutePath } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { ImportModules } from './ImportCustomModule.js'
import { LastUpdatedTimestamp } from './LastUpdatedTimestamp.js'
import { ModuleDeprecationBadge, useModuleDeprecation } from './ModuleDeprecation.js'
import { RefreshModulesList } from './RefreshModulesList.js'

interface VisibleModulesState {
	installed: boolean
	available: boolean
	availableDeprecated: boolean
}

interface ModulesListProps {
	doManageModule: (moduleInfo: ModuleTypeAndIdPair | null) => void
	selectedModuleInfo: ModuleTypeAndIdPair | null
}

export interface ModuleTypeAndIdPair {
	moduleType: ModuleInstanceType
	moduleId: string
}

export const ModulesList = observer(function ModulesList({ doManageModule, selectedModuleInfo }: ModulesListProps) {
	const { modules } = useContext(RootAppStoreContext)

	const visibleModules = useTableVisibilityHelper<VisibleModulesState>('modules_visible', {
		installed: true,
		available: false,
		availableDeprecated: false,
	})

	const [filterType, setFilterType] = useState<ModuleInstanceType | null>(null)
	const [filter, setFilter] = useState('')
	const filterName = (() => {
		if (filterType === null) return ' '
		switch (filterType) {
			case ModuleInstanceType.Connection:
				return ' Connection '
			case ModuleInstanceType.Surface:
				return ' Surface '
			default:
				assertNever(filterType)
				return ' '
		}
	})()

	//  A module can support several devices: useAllModuleProducts returns the list of devices, so some modules are represented by several entries here.
	const allProducts = useAllModuleProducts(null, true, true).filter((p) => !filterType || filterType === p.moduleType)
	const typeProducts = allProducts.filter((p) => {
		let isVisible = false
		if (p.installedInfo) {
			if (
				(p.installedInfo.installedVersions.length > 0 ||
					p.installedInfo.devVersion ||
					p.installedInfo.builtinVersion) &&
				visibleModules.visibility.installed
			)
				isVisible = true
		}
		if (
			p.storeInfo &&
			visibleModules.visibility.available &&
			(visibleModules.visibility.availableDeprecated || !p.storeInfo.deprecationReason) // only show deprecated ones when the flag is enabled
		)
			isVisible = true

		return isVisible
	})

	const includeStoreModules = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			visibleModules.toggleVisibility('available', true)
		},
		[visibleModules]
	)

	let components: React.JSX.Element[] = []
	try {
		const searchResults = filterProducts(typeProducts, filter, true)

		const candidatesObj: Record<string, React.JSX.Element> = {}
		for (const moduleInfo of searchResults) {
			candidatesObj[moduleInfo.moduleId] = (
				<ModulesListRow
					key={moduleInfo.moduleId}
					moduleInfo={moduleInfo}
					doManageModule={doManageModule}
					isSelected={
						!!selectedModuleInfo &&
						moduleInfo.moduleId === selectedModuleInfo.moduleId &&
						moduleInfo.moduleType === selectedModuleInfo.moduleType
					}
				/>
			)
		}

		if (!filter) {
			components = Object.entries(candidatesObj)
				.sort((a, b) => {
					const aName = a[0].toLocaleLowerCase()
					const bName = b[0].toLocaleLowerCase()
					if (aName < bName) return -1
					if (aName > bName) return 1
					return 0
				})
				.map((c) => c[1])
		} else {
			components = Object.entries(candidatesObj).map((c) => c[1])
		}
	} catch (e) {
		console.error('Failed to compile candidates list:', e)

		components = []
		components.push(
			<tr key="module-list-build-error">
				<td colSpan={4}>
					<StaticAlert color="warning" role="alert">
						Failed to build list of modules:
						<br />
						{e?.toString()}
					</StaticAlert>
				</td>
			</tr>
		)
	}

	const moduleKey = (p: FuzzyProduct) => `${p.moduleType}:${p.moduleId}`
	const modulesCount = new Set(allProducts.map(moduleKey)).size
	const hiddenCount = modulesCount - new Set(typeProducts.map(moduleKey)).size

	return (
		<div className="flex-column-layout">
			<div className="fixed-header">
				<h4 className="button-inline">
					Manage Modules
					<ContextHelpButton action="/user-guide/config/modules" />
				</h4>
				<p className="mb-2">
					<strong>
						Companion can work with over {modulesCount} different{filterName}modules
					</strong>{' '}
					and the list grows every day.
				</p>
				<p>
					View and manage your installed modules, or search for new ones to support additional devices. Can't find your
					device?{' '}
					<a target="_blank" href={makeAbsolutePath('/user-guide/config/modules')}>
						Check our guidance for getting device support
					</a>
					.<br />
					For offline systems, download module bundles from the{' '}
					<a href="https://l.companion.free/q/lp68nsiV4" target="_blank">
						Bitfocus website
					</a>
					.
				</p>

				<ImportModules />

				<div className="refresh-and-last-updated">
					<RefreshModulesList />
					<LastUpdatedTimestamp timestamp={modules.storeUpdateInfo.lastUpdated} />
				</div>

				<SearchBox filter={filter} setFilter={setFilter} className="mb-2" />
			</div>

			<FilterTypeTabs filterType={filterType} setFilterType={setFilterType} />

			<div className="scrollable-content">
				<Table className="table-tight">
					<thead>
						<tr>
							<th colSpan={3}>
								Module
								<ButtonGroup className="table-header-buttons">
									<VisibilityButton {...visibleModules} keyId="installed" color="success" label="Installed" />
									<VisibilityButton {...visibleModules} keyId="available" color="warning" label="Available" />
									<VisibilityButton
										{...visibleModules}
										keyId="availableDeprecated"
										color="primary"
										label="Deprecated"
									/>
								</ButtonGroup>
							</th>
						</tr>
					</thead>
					<tbody>
						{components}
						{hiddenCount > 0 && (
							<tr>
								<td colSpan={4} style={{ padding: '10px 5px' }}>
									<FontAwesomeIcon icon={faEyeSlash} style={{ marginRight: '0.5em', color: 'red' }} />
									<strong>{hiddenCount} Modules are ignored</strong>. <br /> Enable{' '}
									{(visibleModules.visibility.installed ? '' : '"Installed" ') +
										(visibleModules.visibility.available ? '' : '"Available" ') +
										(visibleModules.visibility.availableDeprecated ? '' : '"Deprecated"') +
										' to include them in the search'}
								</td>
							</tr>
						)}

						{modules.count === 0 && !visibleModules.visibility.available && (
							<tr>
								<td colSpan={4}>
									<NonIdealState icon={faPlug}>
										You don't have any modules installed yet. <br />
										Try adding something from the list <span className="xl:hidden">below</span>
										<span className="hidden xl:inline">to the right</span>.
									</NonIdealState>
								</td>
							</tr>
						)}

						{components.length === 0 && allProducts.length > 0 && !!filter && !visibleModules.visibility.available && (
							<tr>
								<td colSpan={4}>
									<NonIdealState icon={faPlug}>
										No modules match your search.
										<br />
										{!visibleModules.visibility.available && (
											<a href="#" onClick={includeStoreModules}>
												Click here to include modules from the store
											</a>
										)}
									</NonIdealState>
								</td>
							</tr>
						)}
					</tbody>
				</Table>
			</div>
		</div>
	)
})

interface ModulesListRowProps {
	moduleInfo: FuzzyProduct
	doManageModule: (moduleInfo: ModuleTypeAndIdPair | null) => void
	isSelected: boolean
}

const ModulesListRow = observer(function ModulesListRow({
	moduleInfo,
	doManageModule,
	isSelected,
}: ModulesListRowProps) {
	const { helpViewer } = useContext(RootAppStoreContext)

	// No single version is in context in this list, so only the module as a whole can be deprecated here
	const deprecation = useModuleDeprecation(moduleInfo.moduleType, moduleInfo.moduleId, null)

	const doShowHelp = useCallback(() => {
		if (!moduleInfo.helpUrl) return
		const latestVersionName =
			moduleInfo.installedInfo?.stableVersion?.versionId ?? moduleInfo.installedInfo?.betaVersion?.versionId ?? ''
		helpViewer.current?.showFromUrl(moduleInfo.moduleType, moduleInfo.moduleId, latestVersionName, moduleInfo.helpUrl)
	}, [helpViewer, moduleInfo])

	const doEdit = () => {
		if (!moduleInfo) {
			return
		}

		doManageModule({ moduleId: moduleInfo.moduleId, moduleType: moduleInfo.moduleType })
	}

	// const openBugUrl = useCallback(() => {
	// 	const url = moduleInfo?.bugUrl
	// 	if (url) windowLinkOpen({ href: url })
	// }, [moduleInfo])

	// const moduleVersion = getModuleVersionInfoForConnection(moduleInfo, connection)

	let icon: IconDefinition | null = null
	let iconTitle: string | null = null
	switch (moduleInfo.moduleType) {
		case ModuleInstanceType.Connection:
			icon = faPlug
			iconTitle = 'Connection Module'
			break
		case ModuleInstanceType.Surface:
			icon = faGamepad
			iconTitle = 'Surface Module'
			break
		default:
			assertNever(moduleInfo.moduleType)
			break
	}

	return (
		<tr
			className={classNames({
				'connectionlist-selected': isSelected,
			})}
		>
			<td onClick={doEdit} className="cursor-pointer compact">
				{icon && (
					<span title={iconTitle ?? ''}>
						<FontAwesomeIcon icon={icon} />
					</span>
				)}
			</td>
			<td onClick={doEdit} className="cursor-pointer">
				{deprecation && <ModuleDeprecationBadge deprecation={deprecation} className="me-1" />}
				{moduleInfo.name}
			</td>
			<td className="compact">
				<Button onMouseDown={doShowHelp} title="Show Help" disabled={!moduleInfo.helpUrl}>
					<FontAwesomeIcon icon={faQuestionCircle} />
				</Button>
			</td>
		</tr>
	)
})

interface FilterTypeTabsProps {
	filterType: ModuleInstanceType | null
	setFilterType: (type: ModuleInstanceType | null) => void
}

function FilterTypeTabs({ filterType, setFilterType }: FilterTypeTabsProps) {
	return (
		<TabArea.Root
			value={filterType}
			onValueChange={(v) => setFilterType(v as ModuleInstanceType | null)}
			className="remote-control-tabs"
		>
			<TabArea.List>
				<TabArea.Tab value={null} title="Show all module types">
					All Modules
				</TabArea.Tab>
				<TabArea.Tab value={ModuleInstanceType.Connection} title="Show only connection modules">
					Connection Modules
				</TabArea.Tab>
				<TabArea.Tab value={ModuleInstanceType.Surface} title="Show only surface modules">
					Surface Modules
				</TabArea.Tab>
			</TabArea.List>
		</TabArea.Root>
	)
}
